import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Save, Loader2, X, Search, Image as ImageIcon } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

/**
 * SponsorsManager — event-bound view. Lists event_sponsors (junction) and
 * allows admin to attach sponsors from the global library, edit event-specific
 * tier/booth/order, and detach.
 */
export default function SponsorsManager({ slug }) {
  const { lang } = useI18n();
  const L = (vi, en) => (lang === "en" ? en : vi);
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const headers = { "X-Admin-Token": token };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [picker, setPicker] = useState(false);
  const [editing, setEditing] = useState(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 1800); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/nail-events/${slug}/event-sponsors`, { headers });
      setItems(r.data.event_sponsors || []);
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  }, [slug]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const attach = async (sponsor_id) => {
    try {
      await api.post(`/admin/nail-events/${slug}/event-sponsors`, { sponsor_id }, { headers });
      setPicker(false); flash(L("Đã gắn", "Attached")); load();
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    }
  };

  const detach = async (es_id) => {
    try {
      await api.delete(`/admin/nail-events/${slug}/event-sponsors/${es_id}`, { headers });
      flash(L("Đã gỡ", "Detached")); load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    try {
      await api.post(`/admin/nail-events/${slug}/event-sponsors/reorder`, { order: next.map((s) => s.id) }, { headers });
    } catch (err) { flash(err?.response?.data?.detail || "Error"); load(); }
  };

  const update = async (es_id, patch) => {
    try {
      await api.put(`/admin/nail-events/${slug}/event-sponsors/${es_id}`, patch, { headers });
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="sponsors-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-charcoal">{L("Nhà Tài Trợ", "Sponsors")}</h2>
          <p className="text-sm text-warmgray-700 mt-1">{L("Gắn nhà tài trợ từ thư viện toàn cầu", "Attach sponsors from the global library")}</p>
        </div>
        <button type="button" onClick={() => setPicker(true)} data-testid="sponsor-attach-btn"
          className="inline-flex items-center gap-1.5 bg-charcoal text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-charcoal-700 transition-all">
          <Plus className="w-4 h-4" /> {L("Gắn Nhà Tài Trợ", "Attach Sponsor")}
        </button>
      </div>

      {toast && <div data-testid="sponsors-toast" className="inline-block bg-emerald2/10 text-emerald2 rounded-xl px-4 py-2 text-sm font-medium mb-4">{toast}</div>}

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-warmgray-500" /></div>
      ) : items.length === 0 ? (
        <div className="bg-warmgray-50 border border-dashed border-warmgray-300 rounded-3xl p-10 text-center">
          <ImageIcon className="w-6 h-6 text-warmgray-500 mx-auto" />
          <p className="mt-3 text-warmgray-700">{L("Chưa có nhà tài trợ nào được gắn.", "No sponsors attached yet.")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((sp, i) => (
            <div key={sp.id} data-testid={`event-sponsor-row-${sp.id}`} className="bg-white rounded-2xl p-4 flex items-center gap-4 border border-warmgray-200">
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`event-sponsor-up-${sp.id}`} className="text-warmgray-500 hover:text-charcoal disabled:opacity-30 p-1"><ChevronUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`event-sponsor-down-${sp.id}`} className="text-warmgray-500 hover:text-charcoal disabled:opacity-30 p-1"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <div className="w-16 h-12 rounded-xl bg-warmgray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                {sp.logo_url ? <img src={sp.logo_url} alt={sp.name} className="max-w-full max-h-full object-contain" /> : <ImageIcon className="w-5 h-5 text-warmgray-500" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-charcoal truncate flex items-center gap-2">{sp.name}
                  {sp.tier && <span className="bg-gold-500 text-charcoal rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{sp.tier}</span>}
                </div>
                <div className="text-xs text-warmgray-700 mt-0.5">{[sp.category, sp.booth && `${L("Gian", "Booth")} ${sp.booth}`].filter(Boolean).join(" · ")}</div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => update(sp.id, { is_visible: !sp.is_visible })} data-testid={`event-sponsor-visible-${sp.id}`}
                  className="p-2 rounded-lg text-warmgray-700 hover:text-charcoal hover:bg-warmgray-100">
                  {sp.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-warmgray-500" />}
                </button>
                <button type="button" onClick={() => setEditing(sp)} data-testid={`event-sponsor-edit-${sp.id}`} className="p-2 rounded-lg text-warmgray-700 hover:text-charcoal hover:bg-warmgray-100">
                  <Save className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => detach(sp.id)} data-testid={`event-sponsor-detach-${sp.id}`} className="p-2 rounded-lg text-warmgray-500 hover:text-[#7A2E2E] hover:bg-[#F5DADA]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {picker && <SponsorPicker existing={items.map((s) => s.sponsor_id)} onClose={() => setPicker(false)} onPick={attach} L={L} />}
      {editing && <SponsorEditDialog initial={editing} onClose={() => setEditing(null)} onSave={async (patch) => { await update(editing.id, patch); setEditing(null); }} L={L} />}
    </div>
  );
}

function SponsorPicker({ existing, onClose, onPick, L }) {
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const headers = { "X-Admin-Token": token };
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sponsors", { headers, params: q ? { q } : {} });
      setItems(r.data.sponsors || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="sponsor-picker">
      <div className="w-full max-w-2xl bg-white rounded-3xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl text-charcoal">{L("Chọn Nhà Tài Trợ", "Pick a Sponsor")}</h3>
          <button type="button" onClick={onClose} data-testid="sponsor-picker-close" className="text-warmgray-500 hover:text-charcoal"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="px-6 pt-4 flex items-center gap-3 border-b border-warmgray-100 pb-4">
          <Search className="w-4 h-4 text-warmgray-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Tìm theo tên...", "Search by name...")} data-testid="sponsor-picker-search"
            className="flex-1 bg-transparent text-sm focus:outline-none" />
        </form>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-warmgray-500" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-warmgray-700">{L("Không có nhà tài trợ. Tạo mới từ Thư Viện trước.", "No sponsors. Create one in the Library first.")}</div>
          ) : (
            <div className="space-y-2">
              {items.map((sp) => {
                const already = existing.includes(sp.id);
                return (
                  <button key={sp.id} type="button" disabled={already} onClick={() => onPick(sp.id)} data-testid={`sponsor-pick-${sp.id}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-2xl border ${already ? "border-warmgray-200 bg-warmgray-50 opacity-60" : "border-warmgray-200 hover:border-emerald2 hover:bg-emerald2/5"}`}>
                    <div className="w-14 h-10 rounded-lg bg-warmgray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                      {sp.logo_url ? <img src={sp.logo_url} alt={sp.name} className="max-w-full max-h-full object-contain" /> : <ImageIcon className="w-4 h-4 text-warmgray-500" />}
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <div className="font-medium text-charcoal truncate">{sp.name}</div>
                      <div className="text-xs text-warmgray-700 truncate">{sp.category || "—"}</div>
                    </div>
                    {already && <span className="text-[10px] uppercase tracking-[0.18em] text-warmgray-500">{L("Đã gắn", "Attached")}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SponsorEditDialog({ initial, onClose, onSave, L }) {
  const [form, setForm] = useState({
    tier: initial.tier || "", booth: initial.booth || "",
    display_order: initial.display_order ?? 0,
    is_visible: initial.is_visible !== false,
    promotion_text: initial.promotion_text || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="event-sponsor-edit-dialog">
      <form onSubmit={async (e) => { e.preventDefault(); setSaving(true); await onSave(form); setSaving(false); }}
        className="w-full max-w-lg bg-white rounded-3xl flex flex-col">
        <div className="px-6 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl text-charcoal">{L("Chi Tiết Tài Trợ", "Sponsor Details")}</h3>
          <button type="button" onClick={onClose} className="text-warmgray-500 hover:text-charcoal" data-testid="event-sponsor-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-3">
          <Input label={L("Cấp (Tier)", "Tier")} value={form.tier} onChange={(v) => set("tier", v)} placeholder="platinum / gold / silver" testId="event-sponsor-tier" />
          <Input label={L("Gian hàng", "Booth")} value={form.booth} onChange={(v) => set("booth", v)} testId="event-sponsor-booth" />
          <Input label={L("Thứ tự hiển thị", "Display order")} type="number" value={form.display_order} onChange={(v) => set("display_order", parseInt(v || "0", 10))} testId="event-sponsor-order" />
          <label className="flex items-center justify-between bg-warmgray-50 border border-warmgray-200 rounded-xl px-3 py-2.5">
            <span className="text-xs font-medium text-warmgray-700">{L("Hiển thị", "Visible")}</span>
            <input type="checkbox" checked={form.is_visible} onChange={(e) => set("is_visible", e.target.checked)} data-testid="event-sponsor-visible-toggle" />
          </label>
          <label className="col-span-2 block">
            <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{L("Văn bản khuyến mãi", "Promotion text")}</span>
            <textarea value={form.promotion_text} onChange={(e) => set("promotion_text", e.target.value)} rows={3} data-testid="event-sponsor-promo"
              className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm" />
          </label>
        </div>
        <div className="px-6 py-4 border-t border-warmgray-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-warmgray-700 hover:text-charcoal" data-testid="event-sponsor-edit-cancel">{L("Hủy", "Cancel")}</button>
          <button type="submit" disabled={saving} data-testid="event-sponsor-edit-save"
            className="inline-flex items-center gap-2 bg-emerald2 text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-emerald2-600 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {L("Lưu", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testId, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}
